import React, { FC, useMemo } from 'react';

import { Heading, Table, TableColumnConfig, useTooltip } from '@weave/design-system';
import { theme } from '@weave/theme';
import { formatDate } from 'helpers/date';
import { useHistory } from 'react-router-dom';
import { PortingQueries } from '../../../apis/porting/porting.queries';
import {
  GetPortingAuditSummaryRecord,
  PortingAuditField,
} from '../../../apis/porting/porting.types';
import { injectParams, ONBOARDING } from '../../../constants/paths';
import { getPortAuditFieldLabel } from '../utils/porting-utils';

interface PortAuditTableProps {
  locationId?: string;
  portingDataId: string;
}

export const PortAuditTable: FC<PortAuditTableProps> = ({
  locationId,
  portingDataId,
}) => {
  const { data = [], isLoading } = PortingQueries.useGetPortingAuditHistorySummary<
    GetPortingAuditSummaryRecord[]
  >(
    {
      portingDataId,
    },
    {
      select: (response) => response.records || [],
    }
  );

  const history = useHistory();

  const navigateToRevisionDetail = (revisionId?: string) => {
    if (!revisionId) return;

    history.push(
      injectParams(ONBOARDING.portingV2AuditDetail, {
        id: locationId,
        portingDataId,
        revisionId,
      })
    );
  };

  const colConfig = useMemo<TableColumnConfig<GetPortingAuditSummaryRecord>[]>(
    () => [
      {
        Header: 'Date',
        accessor: (revisionData) => revisionData,
        id: 'changedAt',
        cellRenderer: ({ changedAt }) => formatDate(changedAt, 'llll', true),
      },
      {
        Header: 'User',
        accessor: 'changedBy',
        id: 'changedBy',
      },
      {
        Header: 'Updated',
        accessor: (revisionData) => revisionData,
        id: 'portingAuditFields',
        cellRenderer: ({ portingAuditFields }) => (
          <UpdatedAuditFieldsColumn updatedFields={portingAuditFields} />
        ),
      },
    ],
    []
  );

  return (
    <div css={{ display: 'flex', flexDirection: 'column', padding: theme.spacing(1) }}>
      <Heading level={3}>History</Heading>
      <div
        css={{
          border: `1px solid ${theme.colors.neutral20}`,
          borderRadius: theme.borderRadius.medium,
          padding: theme.spacing(1),
        }}
      >
        <Table
          css={{ borderRadius: theme.borderRadius.medium }}
          isLoading={isLoading}
          data={data}
          colConfig={colConfig}
          rowActions={{
            actions: [],
            shouldHover: true,
            onRowClick: (row) => navigateToRevisionDetail(row.revisionId),
          }}
        />
      </div>
    </div>
  );
};

const UpdatedAuditFieldsColumn: FC<{ updatedFields: PortingAuditField[] }> = ({
  updatedFields = [],
}) => {
  const updatedFieldsCount = updatedFields.length;
  const updatedFieldsLabel = updatedFields.map(getPortAuditFieldLabel).join(', ');

  const { triggerProps, Tooltip, tooltipProps } = useTooltip();

  if (updatedFieldsCount > 1) {
    return (
      <>
        <span {...triggerProps}>{updatedFieldsCount} fields</span>
        <Tooltip {...tooltipProps}>{updatedFieldsLabel}</Tooltip>
      </>
    );
  }

  return <>{updatedFieldsLabel}</>;
};
